"use client";

import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useState,
} from "react";
import type { MediaPlayerSeekSegment } from "./media-player-seek-bar";
import { resolvePointerPercentOnTrack } from "./media-player-seek-math";

/** Width of vertical segment boundary markers on seek tracks. */
export const MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME = "w-[3px] shrink-0";

export type { MediaPlayerSegmentBounds } from "./media-player-seek-math";
export {
	resolveMediaPlayerContinuationLockStartPercent,
	resolveMediaPlayerScrubClampPercent,
	resolveMediaPlayerScrubHitWidthPercent,
	resolvePointerPercentOnTrack,
	resolveSegmentPlayheadFillPercent,
} from "./media-player-seek-math";

export interface MediaPlayerSeekSegmentRegion {
	label: string;
	startPercent: number;
	endPercent: number;
	centerPercent: number;
}

export function resolveSegmentLabelAtPercent(
	percent: number,
	segments: readonly MediaPlayerSeekSegment[],
): string | null {
	return resolveSegmentRegionAtPercent(percent, segments)?.label ?? null;
}

/** Segment region under the pointer; `centerPercent` anchors the track tooltip. */
export function resolveSegmentRegionAtPercent(
	percent: number,
	segments: readonly MediaPlayerSeekSegment[],
): MediaPlayerSeekSegmentRegion | null {
	if (segments.length === 0) {
		return null;
	}

	const clampedPointer = Math.max(0, Math.min(100, percent));
	const sorted = [...segments].sort((a, b) => a.percent - b.percent);
	let activeIndex = 0;
	for (let index = 0; index < sorted.length; index += 1) {
		if (sorted[index].percent <= clampedPointer + 0.001) {
			activeIndex = index;
		}
	}

	const active = sorted[activeIndex];
	if (active == null) {
		return null;
	}

	const startPercent = Math.max(0, Math.min(100, active.percent));
	const next = sorted[activeIndex + 1];
	const endPercent =
		next != null
			? Math.max(startPercent, Math.min(100, next.percent))
			: 100;

	return {
		label: active.label,
		startPercent,
		endPercent,
		centerPercent: (startPercent + endPercent) / 2,
	};
}

function resolveTrackElementForPointer(
	event: ReactPointerEvent<HTMLElement>,
	trackBoundsRef?: RefObject<HTMLElement | null>,
): HTMLElement {
	return trackBoundsRef?.current ?? event.currentTarget;
}

interface TrackPointerHandlers {
	onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerMove?: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerUp?: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerCancel?: (event: ReactPointerEvent<HTMLElement>) => void;
}

interface UseMediaPlayerTrackSegmentTooltipResult {
	trackTooltipOpen: boolean;
	trackTooltipLabel: string | null;
	/** Horizontal centre of the hovered segment (0–100), for tooltip placement. */
	trackTooltipAnchorCenterPercent: number | null;
	mergePointerHandlers: (
		handlers: TrackPointerHandlers,
	) => Required<TrackPointerHandlers>;
}

export function useMediaPlayerTrackSegmentTooltip(
	segments: readonly MediaPlayerSeekSegment[],
	trackBoundsRef?: RefObject<HTMLElement | null>,
): UseMediaPlayerTrackSegmentTooltipResult {
	const [tooltipRegion, setTooltipRegion] =
		useState<MediaPlayerSeekSegmentRegion | null>(null);
	const enabled = segments.length > 0;

	const updateRegionFromPointer = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!enabled) {
				return;
			}
			const track = resolveTrackElementForPointer(event, trackBoundsRef);
			const percent = resolvePointerPercentOnTrack(event.clientX, track);
			setTooltipRegion(resolveSegmentRegionAtPercent(percent, segments));
		},
		[enabled, segments, trackBoundsRef],
	);

	const mergePointerHandlers = useCallback(
		(handlers: TrackPointerHandlers): Required<TrackPointerHandlers> => ({
			onPointerDown: (event) => {
				handlers.onPointerDown?.(event);
				updateRegionFromPointer(event);
			},
			onPointerMove: (event) => {
				handlers.onPointerMove?.(event);
				updateRegionFromPointer(event);
			},
			onPointerUp: (event) => {
				handlers.onPointerUp?.(event);
			},
			onPointerLeave: (event) => {
				handlers.onPointerLeave?.(event);
				setTooltipRegion(null);
			},
			onPointerCancel: (event) => {
				handlers.onPointerCancel?.(event);
				setTooltipRegion(null);
			},
		}),
		[updateRegionFromPointer],
	);

	return {
		trackTooltipOpen: enabled && tooltipRegion != null,
		trackTooltipLabel: tooltipRegion?.label ?? null,
		trackTooltipAnchorCenterPercent: tooltipRegion?.centerPercent ?? null,
		mergePointerHandlers,
	};
}
